import gql from "graphql-tag";
import type {
  GeneratedMutation,
  GraphQLResult,
} from "@aws-amplify/api-graphql";
import type {
  ExtractInsuranceCardInput,
  ExtractInsuranceCardResponse,
} from "@rmh/defs";
import { InsuranceCardExtractionStatus, PresignedUrlDestinations } from "@rmh/defs";

const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png"];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
// The presigned `UserData` destination auto-prefixes the caller's sub, so the
// library only supplies the trailing segment of the key.
const INSURANCE_CARDS_PREFIX = "insurance-cards";

export interface ScanInsuranceCardResult {
  status: InsuranceCardExtractionStatus;
  firstName: string | null;
  lastName: string | null;
  memberId: string | null;
  groupNumber: string | null;
  payerName: string | null;
}

interface ExtractInsuranceCardVariables {
  input: ExtractInsuranceCardInput;
}

interface ExtractInsuranceCardData {
  extractInsuranceCard: ExtractInsuranceCardResponse;
}

/**
 * Executes the `extractInsuranceCard` mutation. Matches the signature of
 * data-ops' `fetchGraphQLClient`, which the caller injects so this lower-level
 * package stays free of the Amplify client dependency (data-ops depends on
 * utils, not the other way around).
 */
export type ExtractInsuranceCardFetcher = (
  query: GeneratedMutation<
    ExtractInsuranceCardVariables,
    ExtractInsuranceCardData
  >,
  variables: ExtractInsuranceCardVariables
) => Promise<GraphQLResult<ExtractInsuranceCardData>>;

export interface ScanInsuranceCardParams {
  frontFile: File;
  backFile?: File;
  /** Cognito sub of the caller, used to build the IDOR-guarded S3 keys. */
  userSub: string;
  /** Cognito id token, forwarded to the presigned-URL gateway. */
  token: string;
  fetchExtractInsuranceCard: ExtractInsuranceCardFetcher;
}

export const extractInsuranceCardMutation = gql`
  mutation extractInsuranceCard($input: ExtractInsuranceCardInput!) {
    extractInsuranceCard(input: $input) {
      status
      firstName
      lastName
      memberId
      groupNumber
      payerName
    }
  }
`.loc?.source.body as GeneratedMutation<
  ExtractInsuranceCardVariables,
  ExtractInsuranceCardData
>;

const FAILED_RESULT: ScanInsuranceCardResult = {
  status: InsuranceCardExtractionStatus.Failed,
  firstName: null,
  lastName: null,
  memberId: null,
  groupNumber: null,
  payerName: null,
};

function validateImageFile(file: File, label: "front" | "back"): void {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    throw new Error(
      `Invalid ${label} image type "${file.type}". Only JPEG and PNG are supported.`
    );
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(
      `The ${label} image exceeds the 10MB limit (${file.size} bytes).`
    );
  }
}

/**
 * Requests a presigned PUT URL from the gateway and uploads the file. Throws on
 * any failure — we can't extract without a stored image.
 */
async function uploadInsuranceCardImage(
  fileKey: string,
  file: File,
  token: string
): Promise<void> {
  const apiName = process.env.NEXT_PUBLIC_GATEWAY_URL as string;

  if (!apiName) {
    throw new Error("No API Endpoint found");
  }

  const path = `/v1/pre-signed-url?fileKey=${fileKey}&bucketName=${PresignedUrlDestinations.UserData}`;
  const response = await fetch(`${apiName}${path}`, {
    headers: { Authorization: token },
  });

  if (!response.ok) {
    throw new Error(`Failed to get a presigned URL for "${fileKey}".`);
  }

  const { presignedUrl } = (await response.json()) as {
    presignedUrl?: string;
  };

  if (!presignedUrl) {
    throw new Error(`Missing presigned URL for "${fileKey}".`);
  }

  const uploadResponse = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });

  if (!uploadResponse.ok) {
    throw new Error(`Failed to upload the insurance card image "${fileKey}".`);
  }
}

/**
 * Uploads the insurance card image(s) and runs OCR extraction.
 *
 * - Validates each file (JPEG/PNG, ≤10MB) and **throws** on a violation.
 * - Uploads to `${userSub}/insurance-cards/{front|back}` via the presigned
 *   `UserData` destination; an upload failure **throws**.
 * - Calls the `extractInsuranceCard` mutation. A Textract/service failure
 *   degrades gracefully to `status: FAILED` with null fields (no throw).
 */
export async function scanInsuranceCard({
  frontFile,
  backFile,
  userSub,
  token,
  fetchExtractInsuranceCard,
}: ScanInsuranceCardParams): Promise<ScanInsuranceCardResult> {
  validateImageFile(frontFile, "front");
  if (backFile) {
    validateImageFile(backFile, "back");
  }

  const frontFileKey = `${INSURANCE_CARDS_PREFIX}/front`;
  const backFileKey = backFile ? `${INSURANCE_CARDS_PREFIX}/back` : undefined;

  // Upload failures throw — there's nothing to extract without the image.
  await Promise.all([
    uploadInsuranceCardImage(frontFileKey, frontFile, token),
    backFile && backFileKey
      ? uploadInsuranceCardImage(backFileKey, backFile, token)
      : Promise.resolve(),
  ]);

  const input: ExtractInsuranceCardInput = {
    frontImageKey: `${userSub}/${frontFileKey}`,
    backImageKey: backFileKey ? `${userSub}/${backFileKey}` : null,
  };

  try {
    const response = await fetchExtractInsuranceCard(
      extractInsuranceCardMutation,
      { input }
    );

    const result = response.data?.extractInsuranceCard;
    if (!result) {
      return FAILED_RESULT;
    }

    return {
      status: result.status,
      firstName: result.firstName ?? null,
      lastName: result.lastName ?? null,
      memberId: result.memberId ?? null,
      groupNumber: result.groupNumber ?? null,
      payerName: result.payerName ?? null,
    };
  } catch (error) {
    // Service/Textract failure degrades gracefully rather than throwing.
    console.error("Insurance card extraction failed:", error);
    return FAILED_RESULT;
  }
}
